import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import has from 'lodash/has';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';

import { FounderGuideServiceModule } from 'services/utilities/founderGuide.service';
import { SegmentServiceModule } from 'services/tracking/segment/segment.service';
import { DataServiceModule } from './data.service';
import { SearchServiceModule } from './search.service';

export const ProductServiceModule = angular
    .module('app.service.data.productService', [
        DataServiceModule,
        FounderGuideServiceModule,
        SearchServiceModule,
        SegmentServiceModule
    ]).service('productService', (
        $q,
        $http,
        dataService,
        settings,
        $window,
        founderGuideService
    ) => {
        return {
            save,
            create,
            createImportJob,
            bulkUpload,
            getBulkUploadStatus,
            getProduct,
            getProducts,
            getMinimalProducts,
            getMinimalProductsWithVariants,
            removeProduct,
            copyProduct,
            getProductCount,
            getMetrics,
            getBaseProductSkuItemFromProduct,
            doesProductHaveVariants,
            showListPrice,
            asVariantsWithMetaData,
            createProductExportJob,
            getStatusCodesForFilter
        };

        function save(product) {
            return dataService.save(settings.api.products, product.id, product);
        }

        function create(product) {
            return dataService.create(settings.api.products, product).then((data) => {
                let taskId = 'organize.addProduct';
                let firstProductCreatedKey = 'productsService:firstProductCreated';
                let productCreatedKey = 'productsService:productCreated';
                const MILESTONE = 1;
                const FOUNDERGUIDE_STEP_KEY = 'productDetails:founderGuideStepComplete';

                $window.sessionStorage.setItem(productCreatedKey, true);

                return founderGuideService.completeFounderGuideTask(taskId, firstProductCreatedKey, MILESTONE)
                    .then((taskCompleted) => {
                        if (taskCompleted) $window.sessionStorage.setItem(FOUNDERGUIDE_STEP_KEY, true);
                        return data;
                    });
            });
        }

        function bulkUpload(url, csv) {
            return $http.put(url, atob(csv), {
                headers: { 'Content-Type': 'text/csv' }
            });
        }

        function createImportJob() {
            return dataService.create(settings.api.importJob);
        }

        function getBulkUploadStatus(importerJobId) {
            return dataService.get(settings.api.productBulkUploadStatus, importerJobId);
        }


        function removeProduct(product) {
            if (Object.prototype.hasOwnProperty.call(product, 'update')) {
                product.update();
            }

            return dataService.remove(settings.api.products, product.id);
        }

        function copyProduct(item, nameLength) {
            var copyItem = angular.copy(item);
            copyItem.sku = null;
            copyItem.seo.friendlyName = null;
            copyItem.createdOn = null;
            copyItem.updatedOn = null;
            return dataService.copy(settings.api.products, copyItem, nameLength);
        }

        function getProduct(id) {
            return dataService.get(settings.api.products, id);
        }

        function getProductsCommon(page, count, sort, options, apiRoute) {
            options = options || {};
            page = page || 1;
            count = count || 25;

            options.pageSize = count;
            options.startIndex = (page - 1) * count;

            if (sort && sort.length) {
                options.sortBy = sort[0].replace(' ', '+');
            }

            return dataService.getAll(apiRoute, options);
        }

        function getProducts(page, count, sort, options) {
            return getProductsCommon(page, count, sort, options, settings.api.products);
        }

        function getMinimalProducts(page, count, sort, options) {
            return getProductsCommon(page, count, sort, options, settings.api.productsMinimal);
        }

        function getMinimalProductsWithVariants(page, count, sort, options) {
            const queryStringOptions = options || {};
            if (!has(queryStringOptions, 'include')) {
                queryStringOptions.include = 'variants';
            }
            return getProductsCommon(page, count, sort, queryStringOptions, settings.api.productsMinimal);
        }

        function getProductCount(queryFilter, sort) {
            return dataService.getAll(settings.api.productsMinimal, {
                filter: queryFilter,
                sort: sort ? sort.replace(' ', '+') : null,
                pageSize: 1
            }).then(response => response.totalCount);
        }

        function getBaseProductSkuItemFromProduct(product) {
            if (product.productVariants &&
                angular.isArray(product.productVariants) &&
                product.productVariants.length > 0) {
                return find(product.productVariants, (productVariant) => {
                    return !productVariant.variants || productVariant.variants.length === 0;
                });
            }
        }

        function doesProductHaveVariants(product) {
            if (!product.variantOptions || product.variantOptions.length === 0) {
                return false;
            }
            return true;
        }

        function getMetrics() {
            return dataService.getAll(settings.api.productsMetrics)
                .then(({ activeCount, inactiveCount, newProductCount, trackedSKUCount, outOfStockSKUCount }) => {
                    return {
                        activeCount,
                        inactiveCount,
                        newProductCount,
                        totalProductCount: activeCount + inactiveCount,
                        trackedSKUCount,
                        outOfStockSKUCount
                    };
                });
        }

        function showListPrice(product) {
            return product.pricing ?
                product.pricing.listPrice :
                product.price;
        }

        function asVariantsWithMetaData(products) {
            const wrappedProducts = products.map((product) => {
                const hasVariants = product.productVariants.length > 1;
                if (!hasVariants) {
                    let quantity = reduce(product.productVariants, (sum, variant) => {
                        return sum + variant.quantity;
                    }, 0);
                    let isInventoryTracked = product.productVariants[0].isInventoryTracked;
                    const fulfillmentData = product.productVariants[0].fulfillmentData;
                    return {
                        selected: false,
                        data: product,
                        id: product.id,
                        hasVariants: hasVariants,
                        isInventoryTracked,
                        quantity,
                        sourceSku: fulfillmentData.sourceSku || '',
                        disabled: isInventoryTracked && quantity === 0
                    };
                }

                if (hasVariants) {
                    const variants = product.productVariants.map((variant) => {
                        let p = cloneDeep(product);
                        assign(p, pick(variant, [
                            'sku',
                            'price',
                            'priceOverride',
                            'isInventoryTracked',
                            'quantity',
                            'variants',
                            'fulfillmentData'
                        ]));
                        p.productVariantId = variant.id;
                        return {
                            selected: false,
                            data: p,
                            id: p.id,
                            isVariant: true,
                            isInventoryTracked: variant.isInventoryTracked,
                            quantity: variant.quantity,
                            disabled: variant.isInventoryTracked && variant.quantity === 0,
                            sourceSku: variant.fulfillmentData.sourceSku || ''
                        };
                    });
                    return variants;
                }
                return [];
            });
            return flatten(wrappedProducts);
        }

        function createProductExportJob(selectedProductSkus = []) {
            return dataService.create(settings.api.productExport, { productSkus: selectedProductSkus });
        }

        function getStatusCodesForFilter(filter) {
            return dataService.get(settings.api.productTaxCodes, filter);
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/products.service.js